//* Import the from libraries
import React, { useState, useCallback, useEffect } from "react";

//* Import hooks from react-router-dom
import { useNavigate } from "react-router-dom";

//* Import the List component
import { List } from "../../../component/list/list";

//* Import the Loading component
import { Loading } from "../../../component/common/Loading";

//* Import the useDisciplines hook
import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";

//* Import the AuthAPI service
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Import the UserRole enum
import { UserRole } from "../../../constants/enum/UserRole";

//? Import the Types Discipline and User
import { Discipline } from "../../../hooks/disciplines/types/types";

type User = {
  role: UserRole;
};

var debounce = require("lodash.debounce");

export function DisciplinesView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [me, setMe] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [disciplineToDelete, setDisciplineToDelete] = useState<
    string | null
  >(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [totalDisciplines, setTotalDisciplines] = useState<number>(0);

  const {
    discipline,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteDisciplines,
    refetch,
    changePage,
    isDeleting,
  } = useDisciplines(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  useEffect(() => {
    setTotalDisciplines(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (disciplineToDelete) {
      try {
        const currentPageItems = discipline.length;
        const currentPage = pagination.currentPage;

        await deleteDisciplines(disciplineToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setDisciplineToDelete(null);
      } catch (error) {
        console.error("Error deleting discipline:", error);
      }
    }
  }, [
    disciplineToDelete,
    deleteDisciplines,
    discipline.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setDisciplineToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  // Show loading only when deleting, allow list to render even if user details not yet loaded
  if (isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        hideManager
        search_text="Pesquise pelo nome da disciplinas"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={`Disciplinas (Total: ${totalDisciplines})`}
        not_found_message="Nenhum disciplinas encontrado"
        button_link="/disciplines/create"
        button_text="Adicionar disciplinas"
        deleting_message="Ao deletar este disciplinas, ele não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/disciplines/edit/${id}`)}
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={discipline.map((discipline: Discipline) => ({
          ...discipline,
          key: discipline.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "createdBy", type: "text", header: "Cadastrado por" },
          { name: "createdAt", type: "date", header: "Criado em" },
        ]}
        loading_search={isSearching}
      />
    </div>
  );
}
